import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { allArticles } from '@/mocks/seoContent';

const colorMap: Record<string, { bg: string; text: string }> = {
  primary: { bg: 'bg-primary-100/80', text: 'text-primary-700' },
  accent: { bg: 'bg-accent-100/80', text: 'text-accent-700' },
  secondary: { bg: 'bg-secondary-100/80', text: 'text-secondary-700' },
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SchemaOrg
        data={{
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          image: article.heroImageUrl,
          url: `https://hipobuyspread.casa/article/${article.slug}`,
          datePublished: '2026-07-12',
          dateModified: '2026-07-12',
          author: {
            '@type': 'Organization',
            name: 'Hipobuy Spreadsheet',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Hipobuy Spreadsheet',
            url: 'https://hipobuyspread.casa/',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://hipobuyspread.casa/article/${article.slug}`,
          },
        }}
      />
      {article.faqs && article.faqs.length > 0 && (
        <SchemaOrg
          data={{
            '@type': 'FAQPage',
            mainEntity: article.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }}
        />
      )}
      <SchemaOrg
        data={{
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://hipobuyspread.casa/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: article.title.length > 50 ? article.title.slice(0, 50) + '...' : article.title,
              item: `https://hipobuyspread.casa/article/${article.slug}`,
            },
          ],
        }}
      />
      <Navbar />

      <main className="pt-20 pb-16">
        <article className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-sm font-body text-foreground-500">
                <li><Link to="/" className="hover:text-primary-500 transition-colors">Home</Link></li>
                <li><span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span></li>
                <li><span className="text-foreground-700">{article.title.length > 50 ? article.title.slice(0, 50) + '...' : article.title}</span></li>
              </ol>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-3 py-1 bg-accent-100/80 text-accent-700 text-xs font-label font-semibold rounded-full mb-4">
                Updated July 2026
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base md:text-lg font-body text-foreground-600 leading-relaxed">
                {article.summary}
              </p>
            </header>

            <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-10">
              <img
                src={article.heroImageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {article.sections?.map((section, idx) => (
              <section key={idx} id={`section-${idx}`} className="mb-12">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  {section.heading}
                </h2>
                <div className="space-y-4 text-sm md:text-base font-body text-foreground-700 leading-relaxed">
                  {section.body.split('\n\n').map((paragraph, pIdx) => (
                    <p key={pIdx}>{paragraph}</p>
                  ))}

                  {section.highlight && (
                    <div className="bg-background-100 rounded-lg p-5 md:p-6 my-6">
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-3">
                        <span className="w-5 h-5 inline-flex items-center justify-center mr-1.5"><i className="ri-lightbulb-line text-primary-500"></i></span>
                        Key Takeaway
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {section.highlight}
                      </p>
                    </div>
                  )}

                  {idx === 1 && article.sections && article.sections.length > 1 && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-6">
                      <div className="bg-background-100 rounded-lg p-4 text-center">
                        <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-primary-100/80">
                          <i className="ri-search-line text-primary-600 text-lg"></i>
                        </span>
                        <p className="text-xs font-label font-semibold text-foreground-900">Step 1: Browse</p>
                        <p className="text-xs text-foreground-500 mt-1">Pick a category and scan recent additions</p>
                      </div>
                      <div className="bg-background-100 rounded-lg p-4 text-center">
                        <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-accent-100/80">
                          <i className="ri-bookmark-line text-accent-600 text-lg"></i>
                        </span>
                        <p className="text-xs font-label font-semibold text-foreground-900">Step 2: Save</p>
                        <p className="text-xs text-foreground-500 mt-1">Bookmark categories you check regularly</p>
                      </div>
                      <div className="bg-background-100 rounded-lg p-4 text-center">
                        <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-secondary-100/80">
                          <i className="ri-refresh-line text-secondary-600 text-lg"></i>
                        </span>
                        <p className="text-xs font-label font-semibold text-foreground-900">Step 3: Revisit</p>
                        <p className="text-xs text-foreground-500 mt-1">Check weekly for fresh spreadsheet updates</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            ))}

            {article.table && (
              <section className="mb-12">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Category Comparison Overview
                </h2>
                <div className="overflow-x-auto my-6">
                  <table className="w-full text-sm border-collapse bg-background-50 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-background-200/70">
                        <th className="text-left p-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Category</th>
                        <th className="text-left p-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Type</th>
                        <th className="text-left p-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Update Frequency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {article.table.map((row, idx) => {
                        const colorKeys = ['primary', 'accent', 'secondary'];
                        const colorKey = colorKeys[idx % 3];
                        const cls = colorMap[colorKey];
                        return (
                          <tr key={idx} className="border-b border-background-200/70">
                            <td className="p-3 font-body text-foreground-700">{row.category}</td>
                            <td className="p-3">
                              <span className={`px-2 py-0.5 ${cls.bg} ${cls.text} text-xs rounded-full`}>
                                {row.type}
                              </span>
                            </td>
                            <td className="p-3 font-body text-foreground-600 text-xs">{row.frequency}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {article.faqs && article.faqs.length > 0 && (
              <section id="faq" className="mb-12">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, idx) => (
                    <div key={idx} className="bg-background-100 rounded-lg p-5">
                      <h3 className="text-base font-label font-semibold text-foreground-900 mb-2">
                        {faq.q}
                      </h3>
                      <p className="text-sm font-body text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-label font-semibold text-foreground-900">Ready to start exploring?</p>
                  <p className="text-xs font-body text-foreground-500 mt-0.5">Browse the complete category collection</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="cta-button inline-flex items-center gap-2 px-5 py-2.5 bg-foreground-900 text-white text-sm font-label font-semibold rounded-md hover:bg-foreground-800 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-home-line"></i></span>
                    Back to Home
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="cta-button inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-label font-semibold rounded-md hover:bg-primary-600 transition-colors"
                  >
                    Visit Hipobuy Sheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </div>

            {allArticles.length > 1 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
                  More Articles You Might Enjoy
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {allArticles
                    .filter((a) => a.slug !== slug)
                    .slice(0, 4)
                    .map((related) => (
                      <Link
                        key={related.slug}
                        to={`/article/${related.slug}`}
                        className="group flex items-start gap-4 p-4 bg-background-100 rounded-lg hover:translate-y-[-2px] transition-transform duration-300"
                      >
                        <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                          <img
                            src={related.heroImageUrl}
                            alt={related.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-label font-semibold text-foreground-900 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
                            {related.title}
                          </h3>
                          <p className="text-xs font-body text-foreground-500 mt-1 line-clamp-1">
                            {related.summary}
                          </p>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}